import React, { useState, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { TextField, TableFooter, Button } from "@mui/material";
import { toast } from "react-hot-toast";
import { TrashIcon } from "@heroicons/react/24/outline";
import { PMField } from "../../../../utils/PMField";
import { ResourceLoading } from "../../../../utils/ResourceLoading";
import { PMNotice } from "../../../../utils/notifications";
import ProConfirmation from "../../../../utils/ProConfirmation";
import { t } from "i18next";
import { TWclassNames } from "../../../../utils/Div";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: "none",
  padding: 0,
  margin: `0 0 ${grid}px 0`,

  // change background colour if dragging
  background: isDragging ? "lightgrey" : null,

  // styles we need to apply on draggables
  ...draggableStyle,
});

export default function EmploymentSalariesTable(props) {
  const { setFieldValue, values, isLoading, drag } = props;

  const name = props.name ? props.name : "employee_salaries";

  const rows = values[name];

  const columns = [
    { id: "drag", label: "", minWidth: 10, maxWidth: 5, active: drag },
    {
      id: "valid_from",
      label: "Valid From",
      minWidth: 100,
      active: true,
    },
    {
      id: "position_percentage",
      label: "Position Percentage",
      minWidth: 50,
      align: "left",
      active: true,
    },
    {
      id: "yearly_salary",
      label: "Yearly Salary",
      minWidth: 100,
      align: "left",
      active: true,
    },
    {
      id: "hourly_rate",
      label: "Hourly Rate",
      minWidth: 100,
      align: "left",
      active: true,
    },
  ];

  const orderItems = (rows) => {
    var array = [];

    if (!rows) return;

    rows.map((row, i) => {
      const rowParams = {
        ...row,
        row_order: i,
        draggableId: "id" + row.id,
      };
      array.push(rowParams);
    });
    return array;
  };

  // Title column is only used when group_row is true
  const defaultRows = [
    {
      id: null,
      activity_id: null,
      row_order: null,
      title: "",
    },
  ];

  const [items, setItems] = useState(
    orderItems(rows?.length > 0 ? rows : defaultRows)
  );

  useEffect(() => {
    // Filter out items that is "is_editable" is false
    const filteredItems = items.filter((item) => item.is_editable !== false);

    setFieldValue(name, filteredItems);
  }, [items]);

  const addItemRow = (type) => {
    const newRow = {
      // Generate a random ID for the new row until one is made in the API
      id: null,
      row_order: items.length,
      // Generating a fake ID for draggable, so that we are able to move the row before saving.
      draggableId: "id" + items.length + 1,
    };
    setItems((prevState) => [...prevState, newRow]);
  };

  const onItemRowChange = (e, data) => {
    if (typeof e === "function") {
      e.preventDefault();
    }

    let prevItems = [...items];
    let item = {
      ...prevItems[data?.row_order],
      [e.target.name]: e.target.value,
    };
    prevItems[data?.row_order] = item;
    setItems(prevItems);
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const newItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );

    setItems(orderItems(newItems));
  };

  const [selectedItemRow, setSelectedItemRow] = useState();

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const handleDeleteRow = () => {
    setDeleteConfirmationOpen(false);
    // Delete selectedItem row from array
    if (selectedItemRow) {
      const newArray = items.map((item) =>
        item.id === selectedItemRow.id ||
        item.draggableId === selectedItemRow.draggableId
          ? { ...item, delete: true }
          : item
      );
      setItems(orderItems(newArray));
    }
  };

  const deleteRow = (item) => {
    setSelectedItemRow(item);
    setDeleteConfirmationOpen(true);
  };

  const canSelectActivity = (item) => {
    if (item?.activity?.id) {
      if (item?.activity?.is_project_activity) {
        if (module === "project") {
          return false;
        } else {
          return true;
        }
      } else {
        return true;
      }
    } else {
      return true;
    }
  };

  const canEditTitle = (item) => {
    if (item?.activity?.id) {
      if (item?.activity?.is_project_activity) {
        if (module === "project") {
          return true;
        } else {
          return false;
        }
      } else {
        return false;
      }
    } else {
      return true;
    }
  };

  const itemRow = (item, provided, snapshot) => {
    if (item.delete) return null; // Hide the item if it is marked as deleted
    return (
      <TableRow
        hover
        key={item.id}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
      >
        {drag ? (
          <TableCell>
            <DragHandleIcon />
          </TableCell>
        ) : null}

        <TableCell align="left">
          <TextField
            required
            disabled={item?.is_editable === false}
            variant="standard"
            value={item?.valid_from}
            fullWidth
            type="date"
            name="valid_from"
            onChange={(e) => onItemRowChange(e, item)}
          />
        </TableCell>

        <TableCell align="left">
          <TextField
            disabled={item?.is_editable === false}
            variant="standard"
            value={`${item?.position_percentage || 0}%`}
            fullWidth
            name="position_percentage"
            onChange={(e) => {
              let rawValue = e.target.value.replace("%", "").trim();
              if (!/^\d+$/.test(rawValue)) rawValue = "0"; // Ensure only numeric input
              const value = Math.min(100, Math.max(0, parseInt(rawValue, 10)));
              onItemRowChange(
                { target: { name: "position_percentage", value } },
                item
              );
            }}
          />
        </TableCell>

        <TableCell align="left">
          <TextField
            // label="Description"
            disabled={item?.is_editable === false}
            variant="standard"
            value={item?.yearly_salary}
            fullWidth
            name="yearly_salary"
            multiline
            onChange={(e) => onItemRowChange(e, item)}
          />
        </TableCell>

        <TableCell align="left">
          <TextField
            disabled={item?.is_editable === false}
            variant="standard"
            fullWidth
            name="hourly_rate"
            value={item.hourly_rate}
            onChange={(e) => onItemRowChange(e, item)}
          />
        </TableCell>

        <TableCell align="right">
          <TrashIcon
            className={TWclassNames(
              item.id === null ? "text-red-500" : "text-gray-400",
              "cursor-pointer h-5"
            )}
            onClick={item.id === null ? () => deleteRow(item) : null}
          />
        </TableCell>
      </TableRow>
    );
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <React.Fragment>
      <Table sx={{ width: "100%" }}>
        <TableHead>
          <TableRow>
            {columns.map((column) =>
              column.active ? (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{
                    top: 57,
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    width: column.width,
                  }}
                >
                  {column.label}
                </TableCell>
              ) : null
            )}
          </TableRow>
        </TableHead>

        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                {items?.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.draggableId}
                    index={index}
                  >
                    {(provided, snapshot) => itemRow(item, provided, snapshot)}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>

        <TableFooter>
          <TableRow>
            <TableCell align="left" colSpan={2}>
              <Button onClick={() => addItemRow()}>Ny rad</Button>
            </TableCell>

            <TableCell laft="left" colSpan={3}></TableCell>
          </TableRow>
        </TableFooter>
      </Table>

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={"Are you sure you want to delete this row?"}
        action="delete"
        module="order_item"
        // item_id={confirmation.item_id}
        yesCallback={handleDeleteRow}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />
    </React.Fragment>
  );
}

EmploymentSalariesTable.defaultProps = {
  drag: true,
};
